/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState, useEffect } from 'react'
import { RefreshCw, Check, AlertCircle } from 'lucide-react'
import { useProjectStore } from '@/ui/stores/project-store'
import type { Project } from '@/lib/storage'

interface GitHubSyncButtonProps {
  project: Project
}

export function GitHubSyncButton({ project }: GitHubSyncButtonProps) {
  const { checkGitHubUpdates, updateGitHubProject } = useProjectStore()
  const [checking, setChecking] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [hasUpdates, setHasUpdates] = useState(false)
  const [commitsBehind, setCommitsBehind] = useState<number | undefined>()
  const [updateProgress, setUpdateProgress] = useState('')

  // Check for updates on mount
  useEffect(() => {
    if (project.github) {
      checkForUpdates()
    }
  }, [project.id])

  const checkForUpdates = async () => {
    if (!project.github) return
    
    setChecking(true)
    try {
      const result = await checkGitHubUpdates(project.id)
      if (result) {
        setHasUpdates(result.hasUpdates)
        setCommitsBehind(result.commitsBehind)
      }
    } catch (error) {
      console.error('Failed to check for updates:', error)
    } finally {
      setChecking(false)
    }
  }

  const handleUpdate = async () => {
    if (!project.github) return

    const confirmed = window.confirm(
      `Update "${project.name}" from GitHub?\n\n` +
      `This will download the latest files from ${project.github.owner}/${project.github.repo}.\n\n` +
      (commitsBehind ? `You are ${commitsBehind} commit${commitsBehind > 1 ? 's' : ''} behind.\n\n` : '') +
      `Your current files will be replaced.`
    )

    if (!confirmed) return

    setUpdating(true)
    setUpdateProgress('Starting update...')
    
    try {
      await updateGitHubProject(project.id, (message, progress) => {
        setUpdateProgress(`${message} (${progress}%)`)
      })
      
      setHasUpdates(false)
      setCommitsBehind(0)
      setUpdateProgress('Updated successfully!')
      
      setTimeout(() => {
        setUpdateProgress('')
      }, 3000)
    } catch (error) {
      console.error('Failed to update project:', error)
      setUpdateProgress('Update failed')
      setTimeout(() => {
        setUpdateProgress('')
      }, 3000)
    } finally {
      setUpdating(false)
    }
  }

  if (!project.github) {
    return null
  }

  return (
    <div className="flex items-center gap-2">
      {/* GitHub indicator */}
      <div className="flex items-center gap-1 text-xs text-foreground-muted">
        <span title={`GitHub: ${project.github.owner}/${project.github.repo}`}>
          🐙
        </span>
        <span className="text-[10px]">
          {project.github.owner}/{project.github.repo}
        </span>
      </div>

      {/* Update button */}
      <button
        onClick={hasUpdates ? handleUpdate : checkForUpdates}
        disabled={checking || updating}
        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
          hasUpdates
            ? 'bg-accent/20 text-accent hover:bg-accent/30 border border-accent/50'
            : 'bg-background-tertiary text-foreground-muted hover:bg-background-hover border border-border'
        }`}
        title={
          hasUpdates
            ? `${commitsBehind} commit${commitsBehind !== 1 ? 's' : ''} behind - click to update`
            : 'Check for updates'
        }
      >
        {checking || updating ? (
          <RefreshCw className="w-3 h-3 animate-spin" />
        ) : hasUpdates ? (
          <>
            <AlertCircle className="w-3 h-3" />
            <span>Update ({commitsBehind})</span>
          </>
        ) : (
          <>
            <Check className="w-3 h-3" />
            <span>Up to date</span>
          </>
        )}
      </button>

      {/* Progress message */}
      {updateProgress && (
        <span className="text-xs text-foreground-muted italic">
          {updateProgress}
        </span>
      )}
    </div>
  )
}

